import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import api from '../utils/api';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [canResend, setCanResend] = useState(true);

  // Countdown timer for resend functionality
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0 && !canResend) {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [countdown, canResend]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/api/verify-email', {
        email: email.toLowerCase().trim(),
        verification_code: verificationCode.trim()
      });
      
      setSuccess('Email verified successfully! You can now login. Please wait for account approval.');
      
      // Navigate to login after a delay
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      setError(error.response?.data?.error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!canResend || !email) return;
    
    setLoading(true);
    setError('');
    
    try {
      // You'll need to implement this endpoint
      await api.post('/api/resend-verification', {
        email: email.toLowerCase().trim()
      });
      
      setSuccess('Verification code resent! Check your email.');
      setCanResend(false);
      setCountdown(60); // 60 second cooldown
      
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const formatCodeInput = (value) => {
    // Remove non-digits and limit to 6 characters
    const cleaned = value.replace(/\D/g, '').slice(0, 6);
    // Add spacing for better readability: 123 456
    return cleaned.replace(/(.{3})(.{1,3})/, '$1 $2');
  };

  const handleCodeChange = (e) => {
    const formatted = formatCodeInput(e.target.value);
    setVerificationCode(formatted.replace(/\s/g, '')); // Store without spaces
  };

  return (
    <div className="verify-email-container">
      <div className="verify-email-main">
        {/* Header Section */}
        <div className="verify-header">
          <div className="verify-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1>Email Verification</h1>
          <p className="verify-subtitle">
            Enter the verification code sent to <strong>{email}</strong>
          </p>
        </div>

        {/* Main Form Card */}
        <div className="verify-card">
          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">!</span>
              <div>{error}</div>
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              <span className="alert-icon">✓</span>
              <div>{success}</div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="verify-form">
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-control"
                placeholder="your.email@example.com"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="verificationCode" className="form-label">
                Verification Code
              </label>
              <input
                type="text"
                id="verificationCode"
                value={formatCodeInput(verificationCode)}
                onChange={handleCodeChange}
                className="form-control code-input"
                placeholder="000000"
                maxLength="7"
                autoComplete="one-time-code"
                required
              />
              <div className="input-help">
                Enter the 6-digit code from your email
              </div>
            </div>

            <button 
              type="submit" 
              className="btn btn-primary verify-btn"
              disabled={loading || verificationCode.length !== 6}
            >
              {loading ? (
                <>
                  <div className="spinner"></div>
                  Verifying...
                </>
              ) : (
                'Verify Email'
              )}
            </button>
          </form>

          {/* Resend Section */}
          <div className="resend-section">
            <p>Didn't receive the code?</p>
            <button 
              type="button"
              onClick={handleResendCode}
              className={`btn-link ${!canResend ? 'disabled' : ''}`}
              disabled={!canResend || loading}
            >
              {canResend ? 'Resend Code' : `Resend in ${countdown}s`}
            </button>
          </div>

          {/* Quick Links */}
          <div className="quick-links">
            <Link to="/login" className="link-item">
              Already verified? Sign in
            </Link>
            <Link to="/register" className="link-item">
              Need a new account? Register
            </Link>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-card">
            <h3>What happens next?</h3>
            <ol className="process-list">
              <li>Verify your email address</li>
              <li>Wait for admin approval (1-2 business days)</li>
              <li>Receive approval notification</li>
              <li>Sign in and access your account</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
